import logger from '@eventbrite/client-logger';
import { Button, STYLE_FILL } from '@eventbrite/eds-button';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeElements, StripeError } from '@stripe/stripe-js';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useHandleRedirectedPayment } from './useHandleRedirectedPayment';

export type StripeButtonHandleErrorCallback = (
    errorMsg: GenericLazyString | string,
) => Promise<void>;

export interface StripeButtonProps {
    isSubmitting: boolean;
    hasErrors: boolean;
    firstName?: string;
    lastName?: string;
    email?: string;
    submitFormCb?: (
        token: { confirmationTokenID: string },
        buyer: { email?: string; firstName?: string; lastName?: string },
        stripeRedirectedPaymentCallback: (
            paymentIntentClientSecret: string,
            handleErrorCallback: StripeButtonHandleErrorCallback,
        ) => Promise<void>,
    ) => void;
}

export const StripeButton: React.FC<StripeButtonProps> = ({
    isSubmitting,
    hasErrors,
    firstName,
    lastName,
    email,
    submitFormCb,
}: StripeButtonProps) => {
    const [isLoading, setIsLoading] = useState(isSubmitting && !hasErrors);

    useEffect(() => {
        setIsLoading(isSubmitting && !hasErrors);
    }, [isSubmitting, hasErrors]);

    const stripe = useStripe();
    const elements: StripeElements | null = useElements();
    const stripeRedirectedPaymentCallback = useHandleRedirectedPayment(stripe);
    const stripeIsLoaded = stripe && elements;
    const isButtonDisabled = isLoading || !stripeIsLoaded;

    const handleError = (error?: StripeError) => {
        logger.error(`Stripe error ${error}`);
        setIsLoading(false);
    };

    const handleSubmit = async () => {
        if (!submitFormCb) {
            logger.info('No submit callback provided');
            return;
        }

        if (!stripeIsLoaded) {
            logger.info(
                'Stripe is not yet loaded when "pay" button is clicked',
            );
            return;
        }

        setIsLoading(true);

        const { error: submitError } = await elements.submit();
        if (submitError) {
            handleError(submitError);
            return;
        }

        const { error: paymentDataError, confirmationToken } =
            await stripe.createConfirmationToken({
                elements,
                params: {
                    payment_method_data: {
                        billing_details: {
                            name: `${firstName} ${lastName}`,
                            email,
                        },
                    },
                },
            });

        if (paymentDataError) {
            handleError(paymentDataError);
            return;
        }

        submitFormCb(
            {
                confirmationTokenID: confirmationToken.id,
            },
            {
                email,
                firstName,
                lastName,
            },
            stripeRedirectedPaymentCallback,
        );
    };

    // TODO Consider switching to Marmalade's Button and LoadingButton:
    // https://www.marmaladedesignsystem.com/76b74a45e/p/39b980-button/b/390e08
    // https://www.marmaladedesignsystem.com/76b74a45e/v/0/p/0496c4-loading-button/b/822c66
    // Problem - LoadingButton seems to look different from the old button with isLoading=true
    return (
        <div
            className={classNames({
                'eds-align--left': true,
                'eds-align--right': false,
            })}
        >
            <Button
                onClick={handleSubmit}
                __containerClassName={classNames({
                    'modal-footer-scrollable-on-desktop': true,
                })}
                style={STYLE_FILL}
                disabled={isButtonDisabled}
                keepDisabledStyleWhileLoading={false}
                data-spec="eds-modal__primary-button"
                data-automation="eds-modal__primary-button"
                data-testid="eds-modal__primary-button"
                isLoading={isLoading}
                isFullWidth={true}
            >
                {gettext('Place order with Stripe')}
            </Button>
        </div>
    );
};
